import { Settings04Icon } from '@/components/ui/icons'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select'
import { cn } from '@/helpers/utils'
import { type TKey, useTranslation } from '@/translations/i18n'

export interface RosterFilterOption<T extends string> {
	id: T
	label: TKey
}

interface RosterFilterSelectProps<T extends string> {
	/** The field's name, e.g. "Check-in status". */
	label: TKey
	options: readonly RosterFilterOption<T>[]
	value: T[]
	onChange: (value: T[]) => void
	className?: string
}

/**
 * One facet of a roster header's filter bar: a bordered trigger naming the
 * field, opening a checklist of its values. Several sit side by side, and each
 * narrows the list independently.
 *
 * The trigger keeps showing the **field name** rather than the selection, with a
 * count once something is picked. With more than one facet in the row, which
 * field a control belongs to has to stay readable, and a multi-selection does not
 * fit on a trigger anyway.
 *
 * Every list on these tabs is unpaginated and filtered in the browser, so opening
 * this and ticking a value costs no request.
 */
export function RosterFilterSelect<T extends string>({
	label,
	options,
	value,
	onChange,
	className
}: RosterFilterSelectProps<T>) {
	const { t } = useTranslation()

	return (
		<Select modal multiple onValueChange={onChange} value={value}>
			{/* `[&_svg]:size-5` covers both icons: the design draws the leading
			    settings glyph and the trailing chevron at 20px, and the shared
			    trigger renders its own chevron at 16. */}
			<SelectTrigger
				className={cn(
					'h-10 gap-2 border-grey-400 bg-neutral-white px-3 font-medium text-neutral-900 text-sm shadow-xs [&_svg]:size-5',
					className
				)}
				size='sm'
			>
				<SelectValue>
					<span className='flex items-center gap-2 whitespace-nowrap'>
						<Settings04Icon size={20} />
						{t(label)}
						{value.length > 0 && (
							<span className='text-neutral-500'>
								{`· ${value.length}`}
							</span>
						)}
					</span>
				</SelectValue>
			</SelectTrigger>
			<SelectContent className='min-w-[220px]'>
				{options.map((option) => (
					<SelectItem key={option.id} value={option.id}>
						{t(option.label)}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	)
}
